/**
 * Les feuilles que la barre et le chat posent par-dessus la salle (`TutorLiveLayer.sheet`), une fente :
 *  - « Choisis tes appareils » (les chevrons du micro et de la caméra, le web) : la fenêtre de l'élève
 *    (`StudentLiveDevices` → `<LiveDevicesSheet>`), mêmes onglets, même niveau du micro, même filtre ;
 *  - « Ressources du tuteur » (B-39) : la page de l'onglet (`<TutorResourcesPage>`) dans une feuille,
 *    comme l'ancienne modale du pied (`ModalM` + `TutorResources`) ;
 *  - l'aperçu d'une pièce du chat (`<TutorMediaPreviewModal>`), la visionneuse (`<MediaDisplay>`),
 *    l'enregistreur d'un vocal (`ConversationVoiceRecorder`).
 *
 * Séparé du crochet : il monte des composants du design system, que Jest ne charge pas.
 */
import React from "react"

import { MediaDisplay, Modal } from "../../../../../../design-system"
import { TutorResourcesPage } from "../../../../../../design-system/tutor"
import { TutorMediaPreviewModal } from "../../../../../../design-system/templates/TutorMediaPreviewModal"
import { type UILiveDeviceTab } from "../../../../../../design-system/templates/LiveDevicesSheet"
import { type NoiseFilter, type RoomDevices } from "../../../../../../engine/live/room/roomDevices"
import { ConversationVoiceRecorder } from "../../student/Messaging/ConversationVoiceRecorder"
import { type ConversationViewer } from "../../student/Messaging/useStudentTutorConversationState"
import { StudentLiveDevices } from "../../student/Live/StudentLiveDevices"
import { type TutorChatPreview, type TutorLiveChromeChat } from "./useTutorLiveChromeChat"

type ModalProps = React.ComponentProps<typeof Modal>

const RESOURCES_TITLE = "Ressources du tuteur"

export type TutorLiveChromeSheets = {
    devices: {
        devices: RoomDevices
        tab: UILiveDeviceTab
        onChangeTab: (tab: UILiveDeviceTab) => void
        onClose: () => void
        micMeasurable: boolean
        cameraPreview?: React.ReactNode
        onOpenHelp: () => void
        noise: NoiseFilter | null
    } | null
    resources: (React.ComponentProps<typeof TutorResourcesPage> & { onClose: () => void }) | null
    preview: TutorChatPreview | null
    viewer: ConversationViewer | null
    closeViewer: () => void
    recorder: TutorLiveChromeChat["recorder"]
}

export function renderTutorLiveChromeSheets(sheets: TutorLiveChromeSheets): React.ReactNode {
    const { devices, resources, preview, viewer, recorder } = sheets
    const nodes: React.ReactNode[] = []
    if (devices != null) {
        nodes.push(
            React.createElement(StudentLiveDevices, {
                key: "devices",
                devices: devices.devices,
                tab: devices.tab,
                onChangeTab: devices.onChangeTab,
                // Un seul appareil dans l'onglet : le choix ferme la fenêtre (`MediaDeviceSelectModal`).
                onSelected: (tab: UILiveDeviceTab) => {
                    const kind = tab === "microphone" ? "microphone" : tab === "camera" ? "camera" : "speaker"
                    if (devices.devices[kind].devices.length <= 1) devices.onClose()
                },
                onClose: devices.onClose,
                micMeasurable: devices.micMeasurable,
                focused: false,
                cameraPreview: devices.cameraPreview,
                onOpenHelp: devices.onOpenHelp,
                noise: devices.noise,
            })
        )
    }
    if (resources != null) {
        const { onClose, ...page } = resources
        nodes.push(
            React.createElement(
                Modal,
                // Les enfants passent en troisième argument : le type de la feuille les compte dans ses props.
                {
                    key: "resources",
                    visible: true,
                    onClose,
                    title: RESOURCES_TITLE,
                    fitContent: true,
                } as unknown as ModalProps,
                React.createElement(TutorResourcesPage, { ...page, inSheet: true })
            )
        )
    }
    if (preview != null) {
        nodes.push(React.createElement(TutorMediaPreviewModal, { key: "preview", visible: true, ...preview }))
    }
    if (viewer?.kind === "image") {
        nodes.push(
            React.createElement(MediaDisplay, {
                key: "viewer",
                kind: "image",
                visible: true,
                images: viewer.images,
                initialIndex: viewer.index,
                onClose: sheets.closeViewer,
            })
        )
    }
    if (viewer?.kind === "pdf") {
        nodes.push(
            React.createElement(MediaDisplay, {
                key: "viewer",
                kind: "pdf",
                visible: true,
                uri: viewer.uri,
                onClose: sheets.closeViewer,
            })
        )
    }
    if (recorder != null) nodes.push(React.createElement(ConversationVoiceRecorder, { key: "recorder", ...recorder }))
    return nodes.length > 0 ? React.createElement(React.Fragment, null, ...nodes) : undefined
}
